import { formatCurrency } from "@/utils/ranges.util";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/atoms/chart";
import { colorsFixedPosition } from "@/components/molecules/company/detail/overview/tabs/CompanyDetailOwnerships";

type Funding = {
  id: string;
  amount: number;
  type: string;
  date: string;
};

type YearlyFunding = {
  year: string;
  fundings: Funding[];
};

type Props = {
  chartData: YearlyFunding[];
  dataKeyXAxis?: string;
};

const BarChartMultiple = ({ chartData, dataKeyXAxis = "year" }: Props) => {
  const lastYears = chartData.slice(-5);

  // Process each year's data:
  // - If there are more than 5 funding entries, use the first 5 individually and sum the rest into "other"
  // - Otherwise, only use the existing entries.
  const processedData = lastYears.map((yearData) => {
    const row: Record<string, number | string | null> = { [dataKeyXAxis]: yearData.year };
    const fundings = yearData.fundings;
    if (fundings.length > 5) {
      for (let i = 0; i < 5; i++) {
        row[`funding-${i}`] = fundings[i].amount;
      }
      row["other"] = fundings.slice(5).reduce((sum, f) => sum + f.amount, 0);
    } else {
      for (let i = 0; i < fundings.length; i++) {
        row[`funding-${i}`] = fundings[i].amount;
      }
    }
    return row;
  });

  // Compute the union of keys that exist in the processed data (excluding the x-axis key)
  const keysSet = new Set<string>();
  processedData.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (key !== dataKeyXAxis) {
        keysSet.add(key);
      }
    });
  });

  // Sort the keys so that "funding-*" keys are in order and "other" is always last
  const globalBarKeys = Array.from(keysSet).sort((a, b) => {
    if (a === "other") return 1;
    if (b === "other") return -1;
    const aNum = parseInt(a.split("-")[1], 10);
    const bNum = parseInt(b.split("-")[1], 10);
    return aNum - bNum;
  });

  // For rows missing any key from the global union, assign null so that Recharts doesn't render a bar for them.
  processedData.forEach((row) => {
    globalBarKeys.forEach((key) => {
      if (!(key in row)) {
        row[key] = null;
      }
    });
  });

  const chartConfig = {} satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} className="h-[200px] w-full">
      <BarChart accessibilityLayer data={processedData}>
        <CartesianGrid vertical={false} horizontal={false} />
        <YAxis
          tickCount={3}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={formatCurrency}
          width={80}
        />
        <XAxis dataKey={dataKeyXAxis} tickLine={false} axisLine={true} tickMargin={8} />
        <ChartTooltip
          shared={false}
          cursor={false}
          content={<ChartTooltipContent formatter={(val) => formatCurrency(val as number)} />}
        />
        {globalBarKeys.map((key, index) => (
          <Bar
            key={key}
            dataKey={key}
            stackId="a"
            barSize={40}
            fill={colorsFixedPosition[index % colorsFixedPosition.length]}
          />
        ))}
      </BarChart>
    </ChartContainer>
  );
};

export default BarChartMultiple;
